import { useEffect, useState } from "react";
import { Box, Divider, Link, Stack, alpha, useTheme } from "@mui/material";
import { Back } from "@gisce/oficina-virtual-components";
import { useTranslation } from "react-i18next";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import {
  CONSENT_CHANGED_EVENT,
  isOneTrustEnabled,
  isOneTrustLoaded,
  openCookieSettings,
} from "@/services/consent";

// With OneTrust the link is only useful once its SDK has loaded (it notifies a consent change
// when ready); with the legacy dialog it is always available.
const useCookieSettingsAvailable = () => {
  const settings = useSettingsContext();
  const oneTrust = isOneTrustEnabled(settings);
  const [loaded, setLoaded] = useState(isOneTrustLoaded);

  useEffect(() => {
    if (!oneTrust) return;
    const update = () => setLoaded(isOneTrustLoaded());
    update();
    window.addEventListener(CONSENT_CHANGED_EVENT, update);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, update);
  }, [oneTrust]);

  return !oneTrust || loaded;
};

const linkSx = {
  color: "inherit",
  fontSize: "0.875rem",
  textDecoration: "none",
  "&:hover, &:focus-visible": { textDecoration: "underline" },
};

const Footer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const settings = useSettingsContext();
  const cookieSettingsAvailable = useCookieSettingsAvailable();
  const withBackground = settings?.footer?.background === true;
  const links = settings?.links;

  const legalLinks = [
    { href: links?.cookies, label: t("common:text.cookie_policy_text") },
    { href: links?.privacyPolicy, label: t("common:text.footer_privacy_policy") },
    { href: links?.gdpr, label: t("common:text.footer_gdpr") },
    { href: links?.conditions, label: t("common:text.footer_conditions") },
  ].filter((link) => link.href);

  const items = [
    ...(cookieSettingsAvailable
      ? [
          <Link
            key="cookie-settings"
            component="button"
            type="button"
            className="ov-footer__cookies"
            onClick={openCookieSettings}
            sx={linkSx}
          >
            {t("common:text.cookie_settings_link")}
          </Link>,
        ]
      : []),
    ...legalLinks.map(({ href, label }) => (
      <Link key={href} href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {label}
      </Link>
    )),
  ];

  if (!items.length) return null;

  // Either a band with the header colours (same defaults as the app bar, no shadow) or a
  // separator line over the page background.
  const { primary } = theme.palette;
  const constants = theme.custom?.constants;
  const bandSx = withBackground
    ? {
        bgcolor: constants?.appBarBackgroundColor ?? primary.main,
        color: constants?.appBarTextColor ?? primary.contrastText,
      }
    : { color: primary.main };

  return (
    <Box
      component="footer"
      className={withBackground ? "ov-footer ov-footer--background" : "ov-footer"}
      sx={{ mt: "auto", pt: 4 }}
    >
      <Box sx={bandSx}>
        {/* Same width as the page body (LayoutProvider), with its padding on small screens. */}
        <Back downBreakpointSx={{ px: 4 }} wrapperComponent={Box}>
          <Box
            sx={{
              py: withBackground ? 2.5 : 2,
              ...(!withBackground && { borderTop: `1px solid ${alpha(primary.main, 0.25)}` }),
            }}
          >
            {/* Stacked on mobile so a wrapped line never starts with a divider. */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              rowGap={1}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ color: "inherit", borderColor: "currentColor", mx: 1.5, display: { xs: "none", sm: "block" } }}
                />
              }
            >
              {items}
            </Stack>
          </Box>
        </Back>
      </Box>
    </Box>
  );
};

export default Footer;
